<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";

const EMPTY_STAFF_FORM = () => ({
  firstName: "",
  otherName: "",
  lastName: "",
  email: "",
  phone: "",
  staffType: "academic",
  accessRoleId: "",
  departmentId: "",
  position: "",
  employmentDate: "",
  status: "active",
  password: "",
  confirmPassword: "",
});

const MODULE_CATALOG = {
  dashboard: ["view", "manage"],
  database: ["view", "backup", "restore", "manage"],
  utilities: ["view", "manage"],
  applications: [
    "view",
    "approve",
    "reject",
    "reopen",
    "clear",
    "edit",
    "change-programme",
    "manage-documents",
    "export",
    "manage",
  ],
  admissionPartners: [
    "view",
    "create",
    "edit",
    "deactivate",
    "export",
    "manage",
  ],
  students: ["view", "export", "import-legacy", "manage"],
  academics: ["view", "create", "edit", "delete", "publish", "manage"],
  exams: ["view", "create", "edit", "delete", "publish", "manage"],
  payments: ["view", "approve", "refund", "export", "manage"],
  staff: ["view", "create", "edit", "reset-password", "terminate", "manage"],
  roles: ["view", "create", "edit", "delete", "assign", "manage"],
  reports: ["view", "export", "manage"],
  scholarship: ["view", "edit", "export", "manage"],
  careers: ["view", "create", "edit", "delete", "publish", "manage"],
  notifications: ["view", "create", "edit", "delete", "publish", "manage"],
  settings: ["view", "edit", "manage"],
};

export default {
  name: "StaffMgt",
  data() {
    return {
      staff: [],
      roles: [],
      departments: [],
      allProgramCourses: [],
      selectedStaff: null,
      selectedStaffCourses: [],
      courseSelection: [],
      showStaffForm: false,
      showRoleManager: false,
      showCourseManager: false,
      editingStaffId: null,
      editingRoleId: null,
      staffForm: EMPTY_STAFF_FORM(),
      roleForm: { name: "", description: "", permissions: {} },
      filters: { staffType: "", status: "", role: "", department: "" },
      searchQuery: "",
      searchTimer: null,
      pagination: {
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 1,
      },
      loading: false,
      saving: false,
      initialized: false,
      moduleCatalog: MODULE_CATALOG,
      staffStatuses: [
        "active",
        "inactive",
        "suspended",
        "retired",
        "on_leave",
        "terminated",
      ],
    };
  },
  computed: {
    ...mapGetters(["hasPermission", "getStaff", "isSuperAdministrator"]),
    canManageStaff() {
      return this.hasPermission("staff", "manage");
    },
    canCreateStaff() {
      return (
        this.hasPermission("staff", "create") &&
        this.hasPermission("roles", "assign")
      );
    },
    canEditStaff() {
      return this.hasPermission("staff", "edit");
    },
    canResetPasswords() {
      return this.hasPermission("staff", "reset-password");
    },
    canTerminateStaff() {
      return this.hasPermission("staff", "terminate");
    },
    canViewRoles() {
      return this.hasPermission("roles", "view");
    },
    canCreateRoles() {
      return this.hasPermission("roles", "create");
    },
    pageRange() {
      const total = this.pagination.totalPages;
      const current = this.pagination.currentPage;
      let start = Math.max(1, current - 2);
      const end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index
      );
    },
    paginationInfo() {
      const start = this.pagination.totalItems
        ? (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1
        : 0;
      return {
        start,
        end: Math.min(
          this.pagination.currentPage * this.pagination.itemsPerPage,
          this.pagination.totalItems
        ),
        total: this.pagination.totalItems,
      };
    },
  },
  watch: {
    searchQuery() {
      if (!this.initialized) return;
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.fetchStaff(1), 350);
    },
    "pagination.itemsPerPage"() {
      if (this.initialized) this.fetchStaff(1);
    },
    "filters.staffType"() {
      if (this.initialized) this.fetchStaff(1);
    },
    "filters.status"() {
      if (this.initialized) this.fetchStaff(1);
    },
    "filters.role"() {
      if (this.initialized) this.fetchStaff(1);
    },
    "filters.department"() {
      if (this.initialized) this.fetchStaff(1);
    },
  },
  async mounted() {
    await this.loadInitialData();
  },
  beforeUnmount() {
    window.clearTimeout(this.searchTimer);
  },
  methods: {
    async loadInitialData() {
      this.loading = true;
      try {
        const [roles, departments] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/roles`),
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/departments`),
        ]);
        this.roles = roles.data.data?.roles || [];
        this.departments = Array.isArray(departments.data)
          ? departments.data
          : [];
        this.initialized = true;
        await this.fetchStaff(1);
      } catch (error) {
        this.showError(error, "Failed to load Staff Management");
      } finally {
        this.loading = false;
      }
    },
    async fetchStaff(page = this.pagination.currentPage) {
      this.loading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/staff`,
          {
            params: {
              page,
              limit: this.pagination.itemsPerPage,
              search: this.searchQuery.trim() || undefined,
              staffType: this.filters.staffType || undefined,
              status: this.filters.status || undefined,
              accessRoleId: this.filters.role || undefined,
              departmentId: this.filters.department || undefined,
            },
          }
        );
        this.staff = response.data.data.staff || [];
        const pagination = response.data.data.pagination || {};
        this.pagination.currentPage = pagination.page || page;
        this.pagination.totalPages = pagination.pages || 1;
        this.pagination.totalItems = pagination.total || 0;
      } catch (error) {
        this.showError(error, "Failed to fetch staff");
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.initialized = false;
      this.searchQuery = "";
      this.filters = { staffType: "", status: "", role: "", department: "" };
      this.$nextTick(() => {
        this.initialized = true;
        this.fetchStaff(1);
      });
    },
    fullName(item) {
      const user = item.userId || {};
      return (
        [user.lastName, user.firstName, user.otherName]
          .filter(Boolean)
          .join(" ") || "N/A"
      );
    },
    statusLabel(value) {
      return String(value || "N/A")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    statusClass(status) {
      return (
        {
          active: "text-bg-success",
          inactive: "text-bg-secondary",
          suspended: "text-bg-warning",
          terminated: "text-bg-danger",
          retired: "text-bg-dark",
          on_leave: "text-bg-info",
        }[status] || "text-bg-secondary"
      );
    },
    isProtectedRole(role) {
      return role?.key === "super_administrator";
    },
    canModifyStaff(item) {
      return (
        this.canEditStaff &&
        (!this.isProtectedRole(item?.accessRoleId) || this.isSuperAdministrator)
      );
    },
    canResetStaffPassword(item) {
      return (
        this.canResetPasswords &&
        (!this.isProtectedRole(item?.accessRoleId) || this.isSuperAdministrator)
      );
    },
    canTerminateStaffMember(item) {
      return (
        this.canTerminateStaff &&
        item?.status !== "terminated" &&
        (!this.isProtectedRole(item?.accessRoleId) || this.isSuperAdministrator)
      );
    },
    hasStaffActions(item) {
      return (
        this.canModifyStaff(item) ||
        (this.canManageStaff && item?.staffType === "academic") ||
        this.canResetStaffPassword(item) ||
        this.canTerminateStaffMember(item)
      );
    },
    formatDate(value, input = false) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return input
        ? date.toISOString().slice(0, 10)
        : date.toLocaleDateString("en-GB");
    },
    openCreateStaff() {
      this.editingStaffId = null;
      this.staffForm = EMPTY_STAFF_FORM();
      this.showStaffForm = true;
    },
    openEditStaff(item) {
      this.editingStaffId = item._id;
      this.staffForm = {
        ...EMPTY_STAFF_FORM(),
        firstName: item.userId?.firstName || "",
        otherName: item.userId?.otherName || "",
        lastName: item.userId?.lastName || "",
        email: item.userId?.email || "",
        phone: item.userId?.phone || "",
        staffType: item.staffType,
        accessRoleId: item.accessRoleId?._id || "",
        departmentId: item.departmentId?._id || "",
        position: item.position || "",
        employmentDate: this.formatDate(item.employmentDate, true),
        status: item.status,
      };
      this.showStaffForm = true;
    },
    async saveStaff() {
      this.saving = true;
      try {
        const payload = { ...this.staffForm };
        if (this.editingStaffId) {
          delete payload.password;
          delete payload.confirmPassword;
          delete payload.staffType;
          if (!payload.employmentDate) delete payload.employmentDate;
          await axios.patch(
            `${process.env.VUE_APP_API_BASE_URL}/staff/${this.editingStaffId}`,
            payload
          );
        } else {
          await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/staff`,
            payload
          );
        }
        this.showStaffForm = false;
        await this.fetchStaff(this.pagination.currentPage);
        await Swal.fire(
          "Success",
          this.editingStaffId
            ? "Staff record updated"
            : "Staff account created",
          "success"
        );
      } catch (error) {
        this.showError(error, "Failed to save staff record");
      } finally {
        this.saving = false;
      }
    },
    async viewStaff(item) {
      try {
        const [profile, courses] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/staff/${item._id}`),
          axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/staff/${item._id}/courses`
          ),
        ]);
        this.selectedStaff = profile.data.data.staff;
        this.selectedStaffCourses = courses.data.data || [];
      } catch (error) {
        this.showError(error, "Failed to load staff details");
      }
    },
    async terminate(item) {
      const confirmation = await Swal.fire({
        title: `Terminate ${item.staffNumber}?`,
        text: "This revokes every active session while retaining the employment record.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Terminate",
      });
      if (!confirmation.isConfirmed) return;
      try {
        await axios.delete(
          `${process.env.VUE_APP_API_BASE_URL}/staff/${item._id}`
        );
        this.selectedStaff = null;
        await this.fetchStaff(this.pagination.currentPage);
        await Swal.fire(
          "Terminated",
          "Staff access has been revoked",
          "success"
        );
      } catch (error) {
        this.showError(error, "Failed to terminate staff access");
      }
    },
    async resetPassword(item) {
      const result = await Swal.fire({
        title: `Reset password for ${item.staffNumber}`,
        html: '<input id="newPassword" type="password" class="swal2-input" placeholder="New password"><input id="confirmPassword" type="password" class="swal2-input" placeholder="Confirm password">',
        showCancelButton: true,
        confirmButtonText: "Reset Password",
        preConfirm: () => {
          const password = document.getElementById("newPassword")?.value || "";
          const confirmPassword =
            document.getElementById("confirmPassword")?.value || "";
          if (!password || password !== confirmPassword) {
            Swal.showValidationMessage("Passwords are required and must match");
            return false;
          }
          return { password, confirmPassword };
        },
      });
      if (!result.isConfirmed) return;
      try {
        await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/staff/${item._id}/reset-password`,
          result.value
        );
        await Swal.fire(
          "Success",
          "Password reset and active sessions revoked",
          "success"
        );
      } catch (error) {
        this.showError(error, "Failed to reset password");
      }
    },
    resetRoleForm() {
      this.editingRoleId = null;
      this.roleForm = { name: "", description: "", permissions: {} };
    },
    editRole(role) {
      if (role.isProtected) return;
      this.editingRoleId = role._id;
      this.roleForm = {
        name: role.name,
        description: role.description || "",
        permissions: Object.fromEntries(
          (role.modulePermissions || []).map((entry) => [
            entry.module,
            [...entry.permissions],
          ])
        ),
      };
    },
    toggleModule(module, enabled) {
      this.roleForm.permissions[module] = enabled ? ["view"] : [];
    },
    togglePermission(module, permission, checked) {
      const current = this.roleForm.permissions[module] || [];
      if (permission === "manage" && checked) {
        this.roleForm.permissions[module] = [...this.moduleCatalog[module]];
      } else if (checked) {
        this.roleForm.permissions[module] = [
          ...new Set([...current, permission]),
        ];
      } else {
        this.roleForm.permissions[module] = current.filter(
          (item) => item !== permission
        );
      }
    },
    async saveRole() {
      if (this.editingRoleId) {
        const role = this.roles.find((item) => item._id === this.editingRoleId);
        if (role?.isProtected) return;
      }
      const payload = {
        name: this.roleForm.name,
        description: this.roleForm.description,
        modulePermissions: Object.entries(this.roleForm.permissions)
          .filter(([, permissions]) => permissions.length)
          .map(([module, permissions]) => ({ module, permissions })),
      };
      try {
        if (this.editingRoleId)
          await axios.put(
            `${process.env.VUE_APP_API_BASE_URL}/roles/${this.editingRoleId}`,
            payload
          );
        else
          await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/roles`,
            payload
          );
        const roles = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/roles`
        );
        this.roles = roles.data.data.roles || [];
        this.resetRoleForm();
        await Swal.fire("Success", "Access role saved", "success");
      } catch (error) {
        this.showError(error, "Failed to save role");
      }
    },
    async deleteRole(role) {
      if (role.isProtected || role.assignedStaffCount) return;
      const confirmation = await Swal.fire({
        title: `Delete ${role.name}?`,
        icon: "warning",
        showCancelButton: true,
      });
      if (!confirmation.isConfirmed) return;
      try {
        await axios.delete(
          `${process.env.VUE_APP_API_BASE_URL}/roles/${role._id}`
        );
        this.roles = this.roles.filter((item) => item._id !== role._id);
      } catch (error) {
        this.showError(error, "Failed to delete role");
      }
    },
    async openCourseManager(item) {
      try {
        const [allCourses, assigned] = await Promise.all([
          this.allProgramCourses.length
            ? Promise.resolve({ data: this.allProgramCourses })
            : axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-courses`),
          axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/staff/${item._id}/courses`
          ),
        ]);
        this.allProgramCourses = allCourses.data || [];
        this.courseSelection = (assigned.data.data || []).map(
          (course) => course._id
        );
        this.selectedStaff = item;
        this.showCourseManager = true;
      } catch (error) {
        this.showError(error, "Failed to load course assignments");
      }
    },
    courseLabel(course) {
      return [
        course.course?.code,
        course.course?.title,
        course.session?.sessionYear,
        `Y${course.year} S${course.semester}`,
      ]
        .filter(Boolean)
        .join(" · ");
    },
    async saveCourseAssignments() {
      try {
        await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/staff/${this.selectedStaff._id}/assign-courses`,
          { courseIds: this.courseSelection }
        );
        this.showCourseManager = false;
        this.selectedStaff = null;
        await Swal.fire("Success", "Course assignments updated", "success");
      } catch (error) {
        this.showError(error, "Failed to update course assignments");
      }
    },
    showError(error, fallback) {
      Swal.fire("Error", error.response?.data?.message || fallback, "error");
    },
  },
};
</script>

<template>
  <div class="container py-4 text-start">
    <div
      class="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-4"
    >
      <div>
        <h4 class="fw-bold mb-1">Staff Management</h4>
        <p class="text-muted mb-0">
          Manage staff identity, employment, access roles, and assignments.
        </p>
      </div>
      <div v-if="canViewRoles || canCreateStaff" class="d-flex gap-2">
        <button
          v-if="canViewRoles"
          class="btn btn-outline-dark btn-sm"
          @click="showRoleManager = true"
        >
          <i class="bi bi-shield-lock me-1"></i>Manage Roles</button
        ><button
          v-if="canCreateStaff"
          class="btn btn-primary btn-sm"
          @click="openCreateStaff"
        >
          <i class="bi bi-person-plus me-1"></i>Add Staff
        </button>
      </div>
    </div>
    <div class="row g-3 mb-4">
      <div class="col-md-7">
        <div class="input-group">
          <span class="input-group-text bg-white"
            ><i class="bi bi-search"></i></span
          ><input
            v-model="searchQuery"
            class="form-control border-start-0"
            placeholder="Search staff number, name, email, phone, or position"
          />
        </div>
      </div>
      <div class="col-md-5 d-flex justify-content-md-end">
        <div class="dropdown">
          <button
            class="btn btn-outline-secondary btn-sm dropdown-toggle"
            data-bs-toggle="dropdown"
          >
            <i class="bi bi-funnel me-1"></i>Filters
          </button>
          <div
            class="dropdown-menu dropdown-menu-end p-3 filter-menu"
            @click.stop
          >
            <div class="d-flex justify-content-between mb-3">
              <strong>Filter Staff</strong
              ><button class="btn btn-link btn-sm p-0" @click="clearFilters">
                Clear all
              </button>
            </div>
            <label class="small text-muted">Staff Type</label
            ><select
              v-model="filters.staffType"
              class="form-select form-select-sm mb-3"
            >
              <option value="">All types</option>
              <option value="academic">Academic</option>
              <option value="administrative">Administrative</option></select
            ><label class="small text-muted">Access Role</label
            ><select
              v-model="filters.role"
              class="form-select form-select-sm mb-3"
            >
              <option value="">All roles</option>
              <option v-for="role in roles" :key="role._id" :value="role._id">
                {{ statusLabel(role.name) }}
              </option></select
            ><label class="small text-muted">Department</label
            ><select
              v-model="filters.department"
              class="form-select form-select-sm mb-3"
            >
              <option value="">All departments</option>
              <option
                v-for="department in departments"
                :key="department._id"
                :value="department._id"
              >
                {{ department.name }}
              </option></select
            ><label class="small text-muted">Status</label
            ><select
              v-model="filters.status"
              class="form-select form-select-sm"
            >
              <option value="">All statuses</option>
              <option
                v-for="status in staffStatuses"
                :key="status"
                :value="status"
              >
                {{ statusLabel(status) }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <div class="card border-0 shadow-sm">
      <div class="card-body p-0">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-4">Staff Number</th>
              <th>Name</th>
              <th>Email</th>
              <th>Staff Type</th>
              <th>Role</th>
              <th>Status</th>
              <th class="text-end pe-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center py-5">
                <div class="spinner-border"></div>
              </td>
            </tr>
            <tr v-else-if="!staff.length">
              <td colspan="7" class="text-center py-5 text-muted">
                No staff records matched the current filters.
              </td>
            </tr>
            <tr v-for="item in staff" v-else :key="item._id">
              <td class="ps-4">{{ item.staffNumber }}</td>
              <td class="text-capitalize">
                {{ fullName(item)
                }}<small class="d-block text-muted">{{
                  item.position || "No position"
                }}</small>
              </td>
              <td>{{ item.userId?.email }}</td>
              <td>{{ statusLabel(item.staffType) }}</td>
              <td>{{ statusLabel(item.accessRoleId?.name) }}</td>
              <td>
                <span class="badge" :class="statusClass(item.status)">{{
                  statusLabel(item.status)
                }}</span>
              </td>
              <td class="text-end pe-4">
                <div class="dropdown">
                  <button
                    class="btn btn-sm btn-light border"
                    data-bs-toggle="dropdown"
                  >
                    <i class="bi bi-three-dots-vertical"></i>
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end">
                    <li>
                      <button class="dropdown-item" @click="viewStaff(item)">
                        <i class="bi bi-eye me-2"></i>View details
                      </button>
                    </li>
                    <template v-if="hasStaffActions(item)"
                      ><li v-if="canModifyStaff(item)">
                        <button
                          class="dropdown-item"
                          @click="openEditStaff(item)"
                        >
                          <i class="bi bi-pencil me-2"></i>Edit staff
                        </button>
                      </li>
                      <li
                        v-if="canManageStaff && item.staffType === 'academic'"
                      >
                        <button
                          class="dropdown-item"
                          @click="openCourseManager(item)"
                        >
                          <i class="bi bi-book me-2"></i>Assign courses
                        </button>
                      </li>
                      <li v-if="canResetStaffPassword(item)">
                        <button
                          class="dropdown-item"
                          @click="resetPassword(item)"
                        >
                          <i class="bi bi-key me-2"></i>Reset password
                        </button>
                      </li>
                      <li v-if="canTerminateStaffMember(item)">
                        <hr class="dropdown-divider" />
                        <button
                          class="dropdown-item text-danger"
                          @click="terminate(item)"
                        >
                          <i class="bi bi-person-x me-2"></i>Terminate access
                        </button>
                      </li></template
                    >
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="card-footer bg-white d-flex justify-content-between align-items-center flex-wrap gap-3"
      >
        <div class="d-flex gap-2 align-items-center">
          <small>Per page</small
          ><select
            v-model.number="pagination.itemsPerPage"
            class="form-select form-select-sm per-page"
          >
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
        <ul class="pagination pagination-sm mb-0">
          <li
            v-for="page in pageRange"
            :key="page"
            class="page-item"
            :class="{ active: pagination.currentPage === page }"
          >
            <button class="page-link" @click="fetchStaff(page)">
              {{ page }}
            </button>
          </li>
        </ul>
        <small
          >Showing {{ paginationInfo.start }}-{{ paginationInfo.end }} of
          {{ paginationInfo.total }} staff</small
        >
      </div>
    </div>

    <div
      v-if="showStaffForm"
      class="modal-bg"
      @click.self="showStaffForm = false"
    >
      <form
        class="modal-card bg-white rounded shadow"
        @submit.prevent="saveStaff"
      >
        <div class="p-3 border-bottom d-flex justify-content-between">
          <h5>{{ editingStaffId ? "Edit Staff" : "Add Staff" }}</h5>
          <button
            type="button"
            class="btn-close"
            @click="showStaffForm = false"
          ></button>
        </div>
        <div class="p-4 modal-scroll">
          <div class="row g-3">
            <div
              v-for="field in [
                { key: 'firstName', label: 'First Name' },
                { key: 'otherName', label: 'Other Name' },
                { key: 'lastName', label: 'Last Name' },
                { key: 'email', label: 'Email', type: 'email' },
                { key: 'phone', label: 'Phone' },
                { key: 'position', label: 'Position' },
              ]"
              :key="field.key"
              class="col-md-6"
            >
              <label class="form-label">{{ field.label }}</label
              ><input
                v-model="staffForm[field.key]"
                :type="field.type || 'text'"
                class="form-control"
                :required="!['otherName', 'position'].includes(field.key)"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label">Staff Type</label
              ><select
                v-model="staffForm.staffType"
                class="form-select"
                :disabled="Boolean(editingStaffId)"
              >
                <option value="academic">Academic</option>
                <option value="administrative">Administrative</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label">Access Role</label
              ><select
                v-model="staffForm.accessRoleId"
                class="form-select"
                :disabled="
                  Boolean(editingStaffId) && !hasPermission('roles', 'assign')
                "
                required
              >
                <option value="" disabled>Select role</option>
                <option
                  v-for="role in roles.filter((r) => r.active !== false)"
                  :key="role._id"
                  :value="role._id"
                  :disabled="
                    !role.capabilities?.assign &&
                    role._id !== staffForm.accessRoleId
                  "
                >
                  {{ statusLabel(role.name)
                  }}{{ role.isProtected ? " (System)" : "" }}
                </option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label">Department</label
              ><select v-model="staffForm.departmentId" class="form-select">
                <option value="">No department</option>
                <option
                  v-for="department in departments"
                  :key="department._id"
                  :value="department._id"
                >
                  {{ department.name }}
                </option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label">Employment Date</label
              ><input
                v-model="staffForm.employmentDate"
                type="date"
                class="form-control"
              />
            </div>
            <div v-if="editingStaffId" class="col-md-6">
              <label class="form-label">Status</label
              ><select v-model="staffForm.status" class="form-select">
                <option
                  v-for="status in staffStatuses.filter(
                    (s) => s !== 'terminated'
                  )"
                  :key="status"
                  :value="status"
                >
                  {{ statusLabel(status) }}
                </option>
              </select>
            </div>
            <template v-else
              ><div class="col-md-6">
                <label class="form-label">Initial Password</label
                ><input
                  v-model="staffForm.password"
                  type="password"
                  class="form-control"
                  required
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Confirm Password</label
                ><input
                  v-model="staffForm.confirmPassword"
                  type="password"
                  class="form-control"
                  required
                /></div
            ></template>
          </div>
        </div>
        <div class="p-3 border-top text-end">
          <button
            type="button"
            class="btn btn-light me-2"
            @click="showStaffForm = false"
          >
            Cancel</button
          ><button class="btn btn-primary" :disabled="saving">
            {{ saving ? "Saving…" : "Save Staff" }}
          </button>
        </div>
      </form>
    </div>

    <div
      v-if="selectedStaff && !showCourseManager"
      class="modal-bg"
      @click.self="selectedStaff = null"
    >
      <div class="modal-card bg-white rounded shadow">
        <div class="p-3 border-bottom d-flex justify-content-between">
          <div>
            <h5 class="mb-0 text-capitalize">{{ fullName(selectedStaff) }}</h5>
            <small>{{ selectedStaff.staffNumber }}</small>
          </div>
          <button class="btn-close" @click="selectedStaff = null"></button>
        </div>
        <div class="p-4 modal-scroll">
          <div class="row g-3">
            <div class="col-md-6">
              <strong>Email:</strong> {{ selectedStaff.userId?.email }}
            </div>
            <div class="col-md-6">
              <strong>Phone:</strong> {{ selectedStaff.userId?.phone }}
            </div>
            <div class="col-md-6">
              <strong>Type:</strong> {{ statusLabel(selectedStaff.staffType) }}
            </div>
            <div class="col-md-6">
              <strong>Position:</strong> {{ selectedStaff.position || "N/A" }}
            </div>
            <div class="col-md-6">
              <strong>Department:</strong>
              {{ selectedStaff.departmentId?.name || "N/A" }}
            </div>
            <div class="col-md-6">
              <strong>Role:</strong>
              {{ statusLabel(selectedStaff.accessRoleId?.name) }}
            </div>
            <div class="col-md-6">
              <strong>Status:</strong> {{ statusLabel(selectedStaff.status) }}
            </div>
            <div class="col-md-6">
              <strong>Employed:</strong>
              {{ formatDate(selectedStaff.employmentDate) || "N/A" }}
            </div>
          </div>
          <hr />
          <h6>Assigned Courses</h6>
          <p v-if="!selectedStaffCourses.length" class="text-muted">
            No courses assigned.
          </p>
          <ul>
            <li v-for="course in selectedStaffCourses" :key="course._id">
              {{ courseLabel(course) }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div
      v-if="showRoleManager"
      class="modal-bg"
      @click.self="showRoleManager = false"
    >
      <div class="role-card bg-white rounded shadow">
        <div class="p-3 border-bottom d-flex justify-content-between">
          <h5>Role Management</h5>
          <button class="btn-close" @click="showRoleManager = false"></button>
        </div>
        <div class="p-4 modal-scroll">
          <div class="row g-4">
            <div class="col-lg-4">
              <h6>Current Roles</h6>
              <div
                v-for="role in roles"
                :key="role._id"
                class="border rounded p-2 mb-2 d-flex justify-content-between"
              >
                <span>
                  <strong>{{ statusLabel(role.name) }}</strong>
                  <span v-if="role.isProtected" class="badge text-bg-dark ms-2">
                    <i class="bi bi-lock-fill me-1"></i>System role
                  </span>
                  <small class="d-block text-muted">{{
                    role.isProtected
                      ? "Protected role with automatic full-system access"
                      : role.description || "No description"
                  }}</small>
                  <small class="d-block text-muted">
                    {{ role.assignedStaffCount || 0 }} assigned staff
                  </small>
                </span>
                <span v-if="!role.isProtected">
                  <button
                    v-if="role.capabilities?.edit"
                    class="btn btn-sm btn-link"
                    title="Edit role"
                    @click="editRole(role)"
                  >
                    <i class="bi bi-pencil"></i></button
                  ><button
                    class="btn btn-sm btn-link text-danger"
                    :disabled="!role.capabilities?.delete"
                    :title="
                      role.assignedStaffCount
                        ? 'Reassign staff before deleting this role'
                        : role.capabilities?.delete
                        ? 'Delete role'
                        : 'You do not have permission to delete this role'
                    "
                    @click="deleteRole(role)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </span>
                <span
                  v-else
                  class="text-muted"
                  title="System roles cannot be edited or deleted"
                >
                  <i class="bi bi-shield-lock fs-5"></i>
                </span>
              </div>
            </div>
            <div v-if="canCreateRoles || editingRoleId" class="col-lg-8">
              <h6>{{ editingRoleId ? "Edit Role" : "Create Role" }}</h6>
              <div class="row g-2 mb-3">
                <div class="col-md-5">
                  <input
                    v-model="roleForm.name"
                    class="form-control"
                    placeholder="Role name"
                  />
                </div>
                <div class="col-md-7">
                  <input
                    v-model="roleForm.description"
                    class="form-control"
                    placeholder="Description"
                  />
                </div>
              </div>
              <div
                v-for="(permissions, module) in moduleCatalog"
                :key="module"
                class="border rounded p-2 mb-2"
              >
                <div class="form-check">
                  <input
                    :id="`module-${module}`"
                    class="form-check-input"
                    type="checkbox"
                    :checked="roleForm.permissions[module]?.length > 0"
                    @change="toggleModule(module, $event.target.checked)"
                  /><label
                    :for="`module-${module}`"
                    class="form-check-label fw-bold text-capitalize"
                    >{{ module }}</label
                  >
                </div>
                <div
                  v-if="roleForm.permissions[module]?.length"
                  class="d-flex flex-wrap gap-3 ms-4 mt-2"
                >
                  <label
                    v-for="permission in permissions"
                    :key="permission"
                    class="form-check-label"
                    ><input
                      class="form-check-input me-1"
                      type="checkbox"
                      :checked="
                        roleForm.permissions[module]?.includes(permission)
                      "
                      @change="
                        togglePermission(
                          module,
                          permission,
                          $event.target.checked
                        )
                      "
                    />{{ statusLabel(permission) }}</label
                  >
                </div>
              </div>
              <div class="text-end">
                <button class="btn btn-light me-2" @click="resetRoleForm">
                  Reset</button
                ><button class="btn btn-primary" @click="saveRole">
                  Save Role
                </button>
              </div>
            </div>
            <div v-else class="col-lg-8">
              <div class="alert alert-light border mb-0">
                Select a custom role you are permitted to edit. Protected system
                roles are read-only and always have full access.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showCourseManager"
      class="modal-bg"
      @click.self="
        showCourseManager = false;
        selectedStaff = null;
      "
    >
      <div class="modal-card bg-white rounded shadow">
        <div class="p-3 border-bottom d-flex justify-content-between">
          <div>
            <h5>Assign Courses</h5>
            <small
              >{{ selectedStaff?.staffNumber }} ·
              {{ fullName(selectedStaff) }}</small
            >
          </div>
          <button
            class="btn-close"
            @click="
              showCourseManager = false;
              selectedStaff = null;
            "
          ></button>
        </div>
        <div class="p-4 modal-scroll">
          <label
            v-for="course in allProgramCourses"
            :key="course._id"
            class="d-block border-bottom py-2"
            ><input
              v-model="courseSelection"
              class="form-check-input me-2"
              type="checkbox"
              :value="course._id"
            />{{ courseLabel(course) }}</label
          >
        </div>
        <div class="p-3 border-top text-end">
          <button class="btn btn-primary" @click="saveCourseAssignments">
            Save Assignments
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter-menu {
  min-width: 330px;
}
.per-page {
  width: 82px;
}
.modal-bg {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-card {
  width: min(850px, 100%);
  max-height: 94vh;
  overflow: hidden;
}
.role-card {
  width: min(1150px, 100%);
  max-height: 94vh;
  overflow: hidden;
}
.modal-scroll {
  max-height: 80vh;
  overflow: auto;
}
</style>
